import moment from "moment";
import { RootState } from "../root-reducer";
import {
  FlatItem,
  ApiFlatItem,
  ApiChildFlatItem,
  ChildFlatItem,
} from "../../types/general";
import { AllClaims } from "../../types/claim";
import {
  AcePlan,
  AllPlans,
  CorePlan,
  Ace2Plan,
  PlanStatus,
  PlanTypes,
} from "../../types/plan";
import { Ace2WarrantyItem, WarrantyItem } from "../../types/warranty-item";

const mapCorePlan = (payload: unknown): CorePlan => {
  return {
    id: payload["warr_id"],
    planStatus: mapPlanStatus(payload["planStatus"] || payload["PlanStatus"]),
    programName: payload["warr_program_name"],
    dealerId: payload["warr_dealer_id"],
    invoiceId: payload["warr_invoice"],
    number: payload["warr_number"],
    programId: payload["warr_program_id"],
    deliveryDate: payload["warr_delivery_date"]
      ? moment(payload["warr_delivery_date"], "YYYY-MM-DD").toDate()
      : null,
    purchaseDate: payload["warr_purchase_date"]
      ? moment(payload["warr_purchase_date"], "YYYY-MM-DD").toDate()
      : null,
    expirationDate: payload["warr_expiration_date"]
      ? moment(payload["warr_expiration_date"], "YYYY-MM-DD").toDate()
      : null,
    store: payload["warr_stor_loc_name"],
    totalPrice: payload["warr_total_price"],
    warrantyProgramPrice: +payload["warranty_program_price"],
  };
};

export const mapPlanStatus = (status) => {
  const allStatus = {
    Active: PlanStatus.ACTIVE,
    Cancelled: PlanStatus.CANCELLED,
    Expired: PlanStatus.Expired,
    "Cancel Request Dealer Review": PlanStatus.CancelDealerReview,
    "Cancel Request Consumer Review": PlanStatus.CancelCustomerReview,
  };
  return allStatus[status] || null;
};

export const mapApiAce2Plan = (payload: unknown): Ace2Plan => {
  return {
    id: payload["id"],
    title: payload["Title"],
    dealerName: payload["DealerName"],
    planStatus: mapPlanStatus(payload["bp_status"]),
    programName: payload?.["Warranty_Programs"]?.["name"] || "",
    dealerId: payload["dealer_id"],
    pdfLink: payload["fullPDF"],
    dealerLogoUrl: payload["DealerLogoUrl"],
    invoiceId: payload["invoice"],
    number: payload["Name"] || "",
    programId: payload["program_id"],
    programMarketingName: payload["program_marketing_name"] || "",
    deliveryDate: payload["delivery_date"]
      ? moment(payload["delivery_date"], "YYYY-MM-DD").toDate()
      : null,
    purchaseDate: payload["purchase_date"]
      ? moment(payload["purchase_date"], "YYYY-MM-DD").toDate()
      : null,
    expirationDate: payload["expiration_date"]
      ? moment(payload["expiration_date"], "YYYY-MM-DD").toDate()
      : null,
    store: payload["stor_loc"],
    totalPrice: +payload["total_price"],
    warrantyProgramPrice: +payload["warranty_program_price"],
    userExtendedInfoId: +payload["user_extended_info_id"],
    contractHolder: payload["contract_holder"],
    customerId: payload["Contact"]?.["id"],
    customerName:
      payload?.["Contact"]?.["name"] || payload["Contact"]?.["Full_Name"],
    internallyGenerated: false,
    planType: PlanTypes.ACE2,
    warrantyItems:
      payload["Line_Items"] && Array.isArray(payload["Line_Items"])
        ? payload["Line_Items"]?.map((item): Ace2WarrantyItem => {
            return {
              id: item["id"],
              name: item["item_type"],
              warranty_line_item_guid: item["warranty_line_item_guid"],
              color: item["color"],
              itemOrigin: item["itemOrigin"],
              manufacturer: item["manufact"],
              material: item["material"],
              itemDescription: item["item_descr"],
              vendor: item["vendor"],
              sku: item["sku"],
              itemPrice: item["item_price"],
              modelNumber: item["model_num"],
              quantity: item["quantity"],
              returned: item["returned"],
              full_replacement: item["full_repl"],
            };
          })
        : [],
  };
};

export const mapApiAcePlan = (payload: unknown): AcePlan => {
  return {
    ...mapCorePlan(payload),
    id: +payload["warr_id"],
    aceMigrated: 1,
    customerName:
      `${payload["cust_fname"] || ""} ${payload["cust_lname"] || ""}`.trim() ||
      "Unknown Customer",
    customerId: payload["cust_id"],
    planType: PlanTypes.ACE,
    warrantyItems: payload["warrantyItems"]?.map(mapApiWarrantyItem) || [],
    internallyGenerated: false,
  };
};

export const mapApiWarrantyItem = (payload: unknown): WarrantyItem => {
  return {
    id: +payload["id"],
    trackKey: payload["trackKey"],
    itemOrigin: payload["itemOrigin"],
    manufacturer: {
      id: payload["manufact"],
      name: payload["manufact_disp"],
    },
    type: {
      id: payload["item_type"],
      name: payload["item_type_disp"],
    },
    material: { id: payload["material"], name: payload["material_disp"] },
    color: { id: payload["color"], name: payload["color_disp"] },
    itemDescription: payload["item_descr"],
    vendor: payload["vendor"],
    sku: payload["sku"],
    itemPrice: payload["item_price"],
    modelNumber: payload["model_num"],
    quantity: payload["quantity"],
    returned: !!payload["returned"],
    full_replacement: !!payload["full_repl"],
  };
};

export const mapApiFlatData = (payload: ApiFlatItem): FlatItem => ({
  id: payload?.id || payload?.ID,
  name: payload.name || payload.NAME,
});

export const mapApiChildFlatData = (
  payload: ApiChildFlatItem
): ChildFlatItem => ({
  id: payload?.id || payload?.ID,
  name: payload.name || payload.NAME,
  parrentId: payload.Parent_Type_ID,
});

export const getPlans = (state: RootState): AllPlans[] => state.user.plans;
export const getClaims = (state: RootState): AllClaims[] => state.user.claims;

export const getItemType = (fileName) => {
  const ext = fileName.split(".").pop() || "";
  return ext.toLowerCase() === "pdf" ? "pdf" : "3003";
};
